import { useCallback, useReducer } from 'react';

import { channelMenuOptions } from '~/components/common/inquiry/drawer/InquiryInfo';
import {
  ContactInfoValue,
  CoverageInfoValue,
  InquiryInfoValue,
  NoteInfoValue,
  PatientInfoValue,
  ReferralInfoValue,
} from '~/components/common/inquiry/drawer/types';

export type UseEditInquiryState = {
  contacts: ContactInfoValue[];
  updateContact: (index: number, v: Partial<ContactInfoValue>) => void;
  addContact: () => void;
  coverage: CoverageInfoValue;
  updateCoverage: (v: Partial<CoverageInfoValue>) => void;
  info: InquiryInfoValue;
  updateInfo: (v: Partial<InquiryInfoValue>) => void;
  note: NoteInfoValue;
  updateNote: (v: Partial<NoteInfoValue>) => void;
  patient: PatientInfoValue;
  updatePatient: (v: Partial<PatientInfoValue>) => void;
  referral: ReferralInfoValue;
  updateReferral: (v: Partial<ReferralInfoValue>) => void;
  resetState: () => void;
};

const initialInquiryInfoValue: InquiryInfoValue = {
  channelName: channelMenuOptions[0].value,
  inquirerRole: 'Patient',
};

const initialPatientInfoValue: PatientInfoValue = {
  isPrimaryContact: true,
};

const initialContactInfoValue: ContactInfoValue = {};

const initialCoverageInfoValue: CoverageInfoValue = {
  coveragePriority: 1,
  isActive: true,
};

const initialReferralInfoValue: ReferralInfoValue = {};

const initialNoteValue: NoteInfoValue = {
  activityWithName: 'Patient',
};

/**
 * What the reducer should do.
 */
enum ReducerActionCommand {
  MERGE,
  ASSIGN,
}

/**
 * An action for a state reducer.
 */
type Action<StateType> = {
  command: ReducerActionCommand;
  payload: StateType;
};

/**
 * Helper to create a reducer merge action.
 * @param payload The data payload
 */
function mergeAction<StateType>(payload: StateType): Action<StateType> {
  return { command: ReducerActionCommand.MERGE, payload };
}

function stateReducer<StateType>(state: StateType, action: Action<StateType>) {
  if (action.command === ReducerActionCommand.MERGE) {
    return { ...state, ...action.payload };
  }
  return action.payload;
}

/**
 * Contact specific reducer commands.
 */
enum ContactReducerActionCommand {
  MERGE,
  ASSIGN,
  RESET,
  ADD,
  MERGE_ALL_BUT,
  MERGE_ALL,
}

/**
 * An action for contacts reducer.
 */
type ContactAction = {
  command: ContactReducerActionCommand;
  index: number;
  contact: Partial<ContactInfoValue>;
};

/**
 * The reducer for contact state
 * @param state The current collection of contacts.
 * @param action The action to take.
 */
function contactStateReducer(
  state: ContactInfoValue[],
  action: ContactAction,
): ContactInfoValue[] {
  const { index, contact, command } = action;

  switch (command) {
    case ContactReducerActionCommand.ADD:
      return [...state, initialContactInfoValue];
    case ContactReducerActionCommand.ASSIGN:
      return state.map((c, i) => {
        return i == index ? contact : c;
      });
    case ContactReducerActionCommand.MERGE:
      return state.map((c, i) => {
        return i == index ? { ...c, ...contact } : c;
      });
    case ContactReducerActionCommand.MERGE_ALL:
      return state.map((v) => {
        return { ...v, ...contact };
      });
    case ContactReducerActionCommand.MERGE_ALL_BUT:
      return state.map((v, i) => {
        return i == index ? v : { ...v, ...contact };
      });
    case ContactReducerActionCommand.RESET:
      return [initialContactInfoValue];
    default:
      break;
  }

  return state;
}

export const useEditInquiry = (): UseEditInquiryState => {
  const [info, dispatchInfo] = useReducer<
    (
      state: InquiryInfoValue,
      newState: Action<Partial<InquiryInfoValue>>,
    ) => InquiryInfoValue
  >(stateReducer, initialInquiryInfoValue);

  const [patient, dispatchPatient] = useReducer<
    (
      state: PatientInfoValue,
      newState: Action<Partial<PatientInfoValue>>,
    ) => PatientInfoValue
  >(stateReducer, initialPatientInfoValue);

  const [contacts, dispatchContact] = useReducer<
    (state: ContactInfoValue[], newState: ContactAction) => ContactInfoValue[]
  >(contactStateReducer, [initialContactInfoValue]);

  const [coverage, dispatchCoverage] = useReducer<
    (
      state: CoverageInfoValue,
      newState: Action<Partial<CoverageInfoValue>>,
    ) => CoverageInfoValue
  >(stateReducer, initialCoverageInfoValue);

  const [referral, dispatchReferral] = useReducer<
    (
      state: ReferralInfoValue,
      newState: Action<Partial<ReferralInfoValue>>,
    ) => ReferralInfoValue
  >(stateReducer, initialReferralInfoValue);

  const [note, dispatchNote] = useReducer<
    (
      state: NoteInfoValue,
      newState: Action<Partial<NoteInfoValue>>,
    ) => NoteInfoValue
  >(stateReducer, initialNoteValue);

  const addContact = useCallback(() => {
    dispatchContact({
      command: ContactReducerActionCommand.ADD,
      contact: {},
      index: 0,
    });
  }, []);

  const updateContact = useCallback(
    (index: number, infoToUpdate: Partial<ContactInfoValue>) => {
      dispatchContact({
        command: ContactReducerActionCommand.MERGE,
        contact: infoToUpdate,
        index,
      });
      if (infoToUpdate.isPrimaryContact === true) {
        dispatchContact({
          command: ContactReducerActionCommand.MERGE_ALL_BUT,
          contact: { isPrimaryContact: false },
          index,
        });
        dispatchPatient(mergeAction({ isPrimaryContact: false }));
        dispatchReferral(mergeAction({ isPrimaryContact: false }));
        dispatchNote(mergeAction({ activityWithName: 'Carer' }));
      }
    },
    [],
  );

  const updateCoverage = useCallback(
    (infoToUpdate: Partial<CoverageInfoValue>) => {
      dispatchCoverage(mergeAction(infoToUpdate));
    },
    [],
  );

  const updateInfo = useCallback((infoToUpdate: Partial<InquiryInfoValue>) => {
    dispatchInfo(mergeAction(infoToUpdate));
  }, []);

  const updateNote = useCallback((infoToUpdate: Partial<NoteInfoValue>) => {
    dispatchNote(mergeAction(infoToUpdate));
  }, []);

  const updatePatient = useCallback(
    (infoToUpdate: Partial<PatientInfoValue>) => {
      dispatchPatient({
        command: ReducerActionCommand.MERGE,
        payload: infoToUpdate,
      });
      if (infoToUpdate.isPrimaryContact === true) {
        dispatchContact({
          command: ContactReducerActionCommand.MERGE_ALL,
          contact: { isPrimaryContact: false },
          index: 0,
        });
        dispatchReferral(mergeAction({ isPrimaryContact: false }));
        dispatchNote(mergeAction({ activityWithName: 'Patient' }));
      }
    },
    [],
  );

  const updateReferral = useCallback(
    (infoToUpdate: Partial<ReferralInfoValue>) => {
      dispatchReferral(mergeAction(infoToUpdate));
      if (infoToUpdate.isPrimaryContact === true) {
        dispatchPatient(mergeAction({ isPrimaryContact: false }));
        dispatchContact({
          command: ContactReducerActionCommand.MERGE_ALL,
          contact: { isPrimaryContact: false },
          index: 0,
        });
        dispatchNote(mergeAction({ activityWithName: 'Provider' }));
      }
    },
    [],
  );

  const resetState = useCallback(() => {
    dispatchInfo({
      command: ReducerActionCommand.ASSIGN,
      payload: initialInquiryInfoValue,
    });
    dispatchPatient({
      command: ReducerActionCommand.ASSIGN,
      payload: initialPatientInfoValue,
    });
    dispatchContact({
      command: ContactReducerActionCommand.RESET,
      contact: {},
      index: 0,
    });
    dispatchCoverage({
      command: ReducerActionCommand.ASSIGN,
      payload: initialCoverageInfoValue,
    });
    dispatchReferral({
      command: ReducerActionCommand.ASSIGN,
      payload: initialReferralInfoValue,
    });
    dispatchNote({
      command: ReducerActionCommand.ASSIGN,
      payload: initialNoteValue,
    });
  }, []);

  return {
    addContact,
    contacts,
    coverage,
    info,
    note,
    patient,
    referral,
    resetState,
    updateContact,
    updateCoverage,
    updateInfo,
    updateNote,
    updatePatient,
    updateReferral,
  };
};
